import { isValidElement } from "react";

import SearchInput from "components/SearchInput";
import ApplyTagsDropdown from "components/tags/ApplyTagsDropdown";
import { NewAdvancedSearch } from "components/NewAdvancedSearch";
import { getFlattenedFilters } from "components/BaseTableV3/table-helpers";
import { DownloadModal } from "components/BaseTableV3/DownloadModal";
import { LastUpdate } from "components/BaseTableV3/LastUpdate";
import { SelectedRowsCount } from "components/BaseTableV3/SelectedRowsCount";
import { BaseTableV3SettingsDrawer } from "components/BaseTableV3/BaseTableV3SettingsDrawer";
import { TableHeaderIcon } from "components/BaseTableV3/TableHeaderIcon";
import useShowDisabledTags from "hooks/useShowDisabledTags";

export function TableTop({
  exportTableName,
  customTableSlug,
  downloadParams = {},
  downloadConfig = {},
  defaultFilterParams = {},
  headerIcon,
  header,
  createButton,
  enableSearch,
  searchQuery = "",
  updateSearch,
  mergeButton,
  actionButtons,
  batchEditButton,
  enableSettings,
  tableName,
  columns,
  customFields,
  customFilters = [],
  tableSettings,
  columnOrder,
  handleUpdateTableSettings,
  handleApplyAdvancedSearch,
  filters = [],
  filterParams = {},
  tagsFilters,
  selectedRowsArr = [],
  rowIdKey,
  className,
  data,
  invalidateData,
  tagsDropdownParams = { visible: false },
  tagsModelType,
  extraButtons = null,
  extra,
  isFetched = false,
  lastUpdate = null,
  handleUncheckAll,
  queryParams = {},
}) {
  const showDisabledTags = useShowDisabledTags();

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:pt-0.5 tw:pb-4 tw:max-md:flex-col tw:max-md:items-start tw:max-md:gap-3.5">
      <div className="tw:flex tw:flex-1 tw:flex-wrap tw:items-center tw:gap-3.5">
        {headerIcon || header ? (
          <div className="tw:flex tw:items-center  tw:gap-3.5">
            {/* An element renders as-is, for icons resolved at runtime such
                  as a custom table's stored icon name. */}
            {headerIcon && !isValidElement(headerIcon) ? (
              <TableHeaderIcon icon={headerIcon} />
            ) : (
              headerIcon
            )}
            {/* A span with a heading role, because Semantic UI's unlayered
                  h2 rules would override the Tailwind utilities. */}
            <span
              role="heading"
              aria-level={2}
              className="tw:text-sm tw:font-bold"
            >
              {header}
            </span>
          </div>
        ) : null}
        {createButton}
        {enableSearch ? (
          <SearchInput
            name="search"
            query={searchQuery}
            onUpdateQuery={updateSearch}
          />
        ) : null}

        {typeof extraButtons === "function"
          ? extraButtons(extra)
          : extraButtons}
        {selectedRowsArr?.length ? (
          <SelectedRowsCount
            count={selectedRowsArr?.length || 0}
            onRemove={handleUncheckAll}
          />
        ) : undefined}
      </div>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3.5">
        {mergeButton}
        {actionButtons}
        {batchEditButton}
        {filters.length ? (
          <>
            <NewAdvancedSearch
              filters={filters}
              queryParams={queryParams}
              tableName={tableName}
              onApply={handleApplyAdvancedSearch}
            />
          </>
        ) : null}
        {tagsDropdownParams.visible ? (
          <ApplyTagsDropdown
            key={`table-tag-dropdown-${tableName}`}
            rows={data}
            modelType={tagsModelType || className}
            checkedArr={selectedRowsArr}
            onSuccess={invalidateData}
            accessor={rowIdKey}
            iconOnly
            variant={selectedRowsArr.length > 0 ? "secondary" : "outline"}
            disabled={tagsDropdownParams.disabled}
            disabledTooltip={tagsDropdownParams.disabledTooltip}
          />
        ) : null}
        {exportTableName || customTableSlug ? (
          <DownloadModal
            downloadParams={downloadParams}
            downloadConfig={downloadConfig}
            defaultFilterParams={defaultFilterParams}
            searchQuery={searchQuery}
            filters={getFlattenedFilters(filters || [])}
            filterParams={filterParams}
            customFilters={customFilters}
            tagsFilters={tagsFilters}
            exportTableName={exportTableName}
            customTableSlug={customTableSlug}
            visibleColumns={tableSettings?.visibleColumns}
            showDisabledTags={showDisabledTags}
            savedSearchName={""}
            sorted={tableSettings?.sort}
            isDisabled={!isFetched}
          />
        ) : null}
        <LastUpdate lastUpdate={lastUpdate} />
        {enableSettings ? (
          <BaseTableV3SettingsDrawer
            tableName={tableName}
            allColumns={columns}
            customFields={customFields}
            freezedColumns={tableSettings?.freezedColumns}
            visibleColumns={columnOrder}
            updateTableSettings={handleUpdateTableSettings}
            enableCustomFieldsSettings={customFields?.length > 0}
            isLoading={!isFetched}
          />
        ) : null}
      </div>
    </div>
  );
}
